import React, {useEffect, useState} from "react";
import {PageTitle} from "../../../../Components/Landmarks";
import {LayoutFullBleed} from "../../../../Components/Layout";
import {Button, inputStyles, TextField} from "../../../../Components/Inputs";
import {GridCell, gridConstants, GridContainer} from "../../../../Components/Grid";
import {useInput} from "../../../../Components/Inputs/inputUtility";
import {LinkButton, EmailLink} from "../../../../Components/Link";
import {locations} from "../../../../Utilities/Location";
import {allow} from "../../../../Components/Authorize";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {isObjectNullOrEmpty} from "../../../../Utilities/Types/objectUtilities";
import {braillerCatalogApi} from "./braillerCatalogApi";
import {BraillerSearchResultsView} from "./BraillerSearchResultsView";
import {emptyGuid} from "../../../../Utilities/commonUtilities";
import {braillerQueueApi} from "../BraillerQueue/braillerQueueApi";
import {MANAGE_BRAILLER_CATALOG} from "../../../../Components/Authorize/policyEventsConstants";
import type {HandleApiCall} from "../../../../types";
import type {BraillerCatalogItem, BraillerCatalogResponse, BraillerCatalogSearchCriteria} from "./braillerCatalog.types";

interface BraillerCatalogContainerProps {
    braillerCatalogSearchCriteria: BraillerCatalogSearchCriteria;
    handleApiCall: HandleApiCall;
    handleSetBraillerCatalogSearchCriteria: (criteria: BraillerCatalogSearchCriteria) => void;
    redirect: (path: string) => void;
}

export const BraillerCatalogContainer = ({
    braillerCatalogSearchCriteria,
    handleApiCall,
    handleSetBraillerCatalogSearchCriteria,
    redirect,
}: BraillerCatalogContainerProps) => {
    const [catalogResources, setCatalogResources] = useState<BraillerCatalogItem[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const keywordSearch = useInput("");

    const canManageCatalog = allow(MANAGE_BRAILLER_CATALOG);

    const loadCatalogResourcesResult = (result: BraillerCatalogResponse) => {
        setCatalogResources(result?.items || []);
        setTotalRecords(result?.totalCount || 0);
    };

    const getCatalogResources = () => {
        if (isObjectNullOrEmpty(braillerCatalogSearchCriteria)) return;

        const updatedCriteria = {
            ...braillerCatalogSearchCriteria,
            searchTerm: keywordSearch.value,
            loadSearchResults: true,
        };
        handleApiCall(
            () => braillerCatalogApi.getBraillerCatalogResources(updatedCriteria),
            loadCatalogResourcesResult
        );
    };

    const handleEdit = (catalogItemId: string) => {
        redirect(locations.BRAILLER_CATALOG_ITEM.getLink([catalogItemId]).to);
    };

    const handleAddToQueue = (catalogItemId: string) => {
        handleApiCall(
            () => braillerQueueApi.addToQueue(catalogItemId),
            () => {
                getCatalogResources();
            }
        );
    };

    const handleRepairs = (catalogItemId: string) => {
        redirect(locations.BRAILLER_REPAIRS.getLink([catalogItemId]).to);
    };

    const handleSearch = () => {
        if (isObjectNullOrEmpty(braillerCatalogSearchCriteria)) return;
        getCatalogResources();
    };

    const noResults = isArrayNullOrEmpty(catalogResources);

    useEffect(() => {
        if (braillerCatalogSearchCriteria?.loadSearchResults) {
            getCatalogResources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [braillerCatalogSearchCriteria, keywordSearch.value]);

    useEffect(() => {
        if (!isObjectNullOrEmpty(braillerCatalogSearchCriteria)) {
            getCatalogResources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keywordSearch.value]);

    if (!braillerCatalogSearchCriteria) return null;

    return (
        <>
            <PageTitle h1="Brailler Catalog"/>

            <GridContainer>
                <GridCell>
                    <p>
                        Browse and search the FIMC-VI Brailler catalog to find braillers available for loan. Items can
                        be added to your queue for checkout. For repairs, the information to be printed and included in
                        the box with the broken brailler can be found when you submit the repair request or using the
                        page icon under Actions on the Brailler History page. If you have specific questions about an
                        item or loan,
                        please contact{" "}
                        <EmailLink address="information@fimcvi.org" className="-email"/>.
                    </p>
                </GridCell>
            </GridContainer>

            <div className="searchBox margin--bottom">
                <div className="searchBox__title">Search the catalog</div>
                <GridContainer hasMarginX hasMarginY className="searchBox__input">
                    <TextField
                        label="Keyword Search box"
                        labelClass="show-for-sr"
                        large_columns={gridConstants.column.TEN}
                        medium_columns={gridConstants.column.EIGHT}
                        name="txtSearch"
                        placeholder="Title, Model, or Manufacturer"
                        {...keywordSearch}
                    />

                    <GridCell
                        large_columns={gridConstants.column.TWO}
                        medium_columns={gridConstants.column.FOUR}
                    >
                        <Button
                            buttonType={inputStyles.button.types.SEARCH}
                            label="Search"
                            name="btnSearch"
                            onClick={handleSearch}
                            isPrimary
                        />
                    </GridCell>
                </GridContainer>
            </div>

            <GridContainer>
                <GridCell className="text-center">
                    <LinkButton {...locations.BRAILLER_QUEUE.getLink()} />
                    <LinkButton {...locations.BRAILLER_HISTORY.getLink()} />
                    {canManageCatalog && (
                        <LinkButton
                            {...locations.BRAILLER_CATALOG_ITEM.getLink([emptyGuid])}
                            label="Add New Item"
                        />
                    )}
                </GridCell>
            </GridContainer>

            <LayoutFullBleed className="catalog">
                <header>
                    <h2>Search Results</h2>
                </header>

                {catalogResources && !noResults && (
                    <BraillerSearchResultsView
                        catalogResources={catalogResources}
                        catalogSearchCriteria={braillerCatalogSearchCriteria}
                        canManageCatalog={canManageCatalog}
                        handleAddToQueue={handleAddToQueue}
                        handleEdit={handleEdit}
                        handleSetSearchCriteria={handleSetBraillerCatalogSearchCriteria}
                        totalRecords={totalRecords}
                        handleRepairs={handleRepairs}
                    />
                )}
                {noResults && (
                    <p className="text-center">
                        No results found. Please try a different search or contact
                        FIMC-VI at <EmailLink address="information@fimcvi.org" className="-email"/> if you believe this
                        is an
                        error.
                    </p>
                )}
            </LayoutFullBleed>
        </>
    );
};